import { formatDisplayDate } from "./commissionsConstants";

/** Fields that can be saved via updateApplicationStudentCourseDetails */
export const INQUIRY_EDITABLE_KEYS = [
  "studentId",
  "dob",
  "start",
  "end",
  "tuitionFee",
  "commissionableTuitionFee",
];

/** Application / material fees saved via commission-inquiry-fees upsert */
export const INQUIRY_FEE_EDITABLE_KEYS = ["applicationFee", "materialFee"];

/** All fields required before moving an application into inquiry */
export const INQUIRY_REQUIRED_FIELDS = [
  { key: "clientName", label: "Client name" },
  { key: "studentId", label: "Student ID" },
  { key: "dob", label: "DOB" },
  { key: "course", label: "Course name" },
  { key: "provider", label: "Provider" },
  { key: "intake", label: "Intake" },
  { key: "tuitionFee", label: "Tuition" },
  { key: "start", label: "Start" },
  { key: "end", label: "End" },
];

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text && text !== "-" && text !== "—" && text.toLowerCase() !== "null") {
      return text;
    }
  }
  return "";
}

export function toInquiryInputDate(value) {
  if (!value) return "";
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const slash = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    const [, day, month, year] = slash;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }
  return "";
}

export function formatInquiryDisplayDate(value) {
  if (!value) return "";
  return formatDisplayDate(toInquiryInputDate(value) || value) || "";
}

export function resolveInquiryDurationLabel(start, end) {
  const startDate = toInquiryInputDate(start);
  const endDate = toInquiryInputDate(end);
  if (!startDate || !endDate) return "";
  const a = new Date(startDate);
  const b = new Date(endDate);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a) {
    return "";
  }
  const months =
    (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
  if (months <= 0) return "";
  if (months === 1) return "1 month";
  if (months < 12) return `${months} months`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  if (rem === 0) return years === 1 ? "1 year" : `${years} years`;
  return `${years}y ${rem}m`;
}

export function formatInquiryTuition(value) {
  if (value == null || value === "") return "";
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) {
    const text = String(value).trim();
    return text && text !== "0" ? text : "";
  }
  return `$ ${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Normalize inquiry fields from a commissions list application row.
 * Student ID never falls back to client id.
 */
export function getInquiryFieldsFromApplication(application = {}) {
  const clientName = firstNonEmpty(application.clientname, application.client_name);
  const studentId = firstNonEmpty(
    application.student_id,
    application.studentid,
    application.studentId,
    application.client_student_id,
  );
  const dob = toInquiryInputDate(
    firstNonEmpty(
      application.dob,
      application.date_of_birth,
      application.birth_date,
      application.student_dob,
      application.client_dob,
    ),
  );
  const course = firstNonEmpty(
    application.coursename,
    application.course_name,
    application.course?.name,
    application.packagename,
  );
  const provider = firstNonEmpty(
    application.providername,
    application.provider_name,
  );
  const intake = firstNonEmpty(application.intake, application.intake_name);
  const tuitionRaw = firstNonEmpty(
    application.tuition_fee,
    application.tuitionfee,
    application.tuitionFee,
    application.course_tuition_fee,
  );
  const tuitionFee =
    tuitionRaw && Number(tuitionRaw) > 0 ? String(Number(tuitionRaw)) : tuitionRaw || "";
  const commissionableRaw = firstNonEmpty(
    application.commissionable_tuition_fee,
    application.commissionableTuitionFee,
    application.commissionable_tuition,
  );
  const commissionableTuitionFee =
    commissionableRaw && Number(commissionableRaw) > 0
      ? String(Number(commissionableRaw))
      : commissionableRaw || "";
  const applicationFeeRaw = firstNonEmpty(
    application.application_fee,
    application.applicationFee,
  );
  const applicationFee =
    applicationFeeRaw != null && applicationFeeRaw !== ""
      ? String(applicationFeeRaw)
      : "";
  const materialFeeRaw = firstNonEmpty(
    application.material_fee,
    application.materialFee,
  );
  const materialFee =
    materialFeeRaw != null && materialFeeRaw !== ""
      ? String(materialFeeRaw)
      : "";
  const start = toInquiryInputDate(
    firstNonEmpty(
      application.start_date,
      application.startdate,
      application.course_start,
      application.course_start_date,
    ),
  );
  const end = toInquiryInputDate(
    firstNonEmpty(
      application.end_date,
      application.enddate,
      application.course_end,
      application.course_end_date,
    ),
  );

  return {
    clientName,
    studentId,
    dob,
    course,
    provider,
    intake,
    tuitionFee,
    commissionableTuitionFee,
    applicationFee,
    materialFee,
    currency: firstNonEmpty(application.currency) || "AUD",
    start,
    end,
    duration: resolveInquiryDurationLabel(start, end),
    clientId: application.clientid ?? application.client_id ?? null,
    applicationId: application.application_id ?? application.id ?? null,
    providerId: application.providerid ?? application.provider_id ?? null,
    courseId: application.courseid ?? application.course_id ?? null,
    applicationCourseId:
      application.application_course_id ??
      application.applicationcourseid ??
      application.eduappcourse_id ??
      null,
    type: application.category || application.type || "education",
  };
}

export function getMissingInquiryFields(fields = {}) {
  return INQUIRY_REQUIRED_FIELDS.filter(({ key }) => {
    const value = fields[key];
    if (value == null) return true;
    const text = String(value).trim();
    if (!text) return true;
    if (key === "tuitionFee") {
      const num = Number(text);
      return !Number.isFinite(num) || num <= 0;
    }
    return false;
  });
}

/** Batch inquiry: student ID or DOB is enough (not both required). */
export const INQUIRY_BATCH_REQUIRED_FIELDS = [
  { key: "clientName", label: "Student name" },
  { key: "course", label: "Course name" },
  { key: "provider", label: "Provider" },
  { key: "intake", label: "Intake" },
  { key: "start", label: "Course start date" },
  { key: "end", label: "Course end date" },
];

export function hasInquiryIdentity(fields = {}) {
  return (
    Boolean(String(fields.studentId || "").trim()) ||
    Boolean(toInquiryInputDate(fields.dob))
  );
}

export function getMissingInquiryBatchFields(fields = {}) {
  const missing = INQUIRY_BATCH_REQUIRED_FIELDS.filter(({ key }) => {
    const value = fields[key];
    if (value == null) return true;
    const text = String(value).trim();
    return !text;
  });

  if (!hasInquiryIdentity(fields)) {
    missing.push({ key: "studentIdOrDob", label: "Student ID or DOB" });
  }

  return missing;
}

export function isInquiryBatchComplete(fields = {}) {
  return getMissingInquiryBatchFields(fields).length === 0;
}

/** Item statuses that can be emailed to the provider (first send or resend). */
export const SENDABLE_BATCH_ITEM_STATUSES = ["pending", "sent"];

export function isResendBatchItemToProvider(item = {}) {
  return String(item?.item_status || "") === "sent";
}

export function canSendBatchItemToProvider(item = {}, batch = {}) {
  if (!item || !SENDABLE_BATCH_ITEM_STATUSES.includes(item.item_status)) {
    return false;
  }
  return isInquiryBatchComplete(getInquiryFieldsFromBatchItem(item, batch));
}

export function getInquiryFieldsFromBatchItem(item = {}, batch = {}) {
  return getInquiryFieldsFromApplication({
    clientname: item.student_name,
    student_id: item.student_id,
    dob: item.dob,
    coursename: item.course_name,
    providername: item.provider_name,
    intake: item.intake,
    tuition_fee: item.tuition_fee,
    commissionable_tuition_fee: item.commissionable_tuition_fee,
    application_fee: item.application_fee,
    material_fee: item.material_fee,
    start_date: item.start_date,
    end_date: item.end_date,
    clientid: item.client_id,
    application_id: item.application_id,
    providerid: item.provider_id,
    courseid: item.course_id,
    application_course_id: item.application_course_id,
    category: batch?.application_type || "education",
    documents: item.documents,
  });
}

export function batchItemToApplicationShape(item = {}, batch = {}) {
  const fields = getInquiryFieldsFromBatchItem(item, batch);
  return {
    clientname: fields.clientName,
    student_id: fields.studentId,
    dob: fields.dob,
    coursename: fields.course,
    providername: fields.provider,
    intake: fields.intake,
    tuition_fee: fields.tuitionFee,
    commissionable_tuition_fee: fields.commissionableTuitionFee,
    application_fee: fields.applicationFee,
    material_fee: fields.materialFee,
    start_date: fields.start,
    end_date: fields.end,
    clientid: fields.clientId,
    application_id: fields.applicationId,
    providerid: fields.providerId,
    courseid: fields.courseId,
    application_course_id: fields.applicationCourseId,
    category: fields.type,
    documents: item.documents,
    _batchItemId: item.id,
  };
}

export function patchBatchItemFromInquiryFields(item = {}, fields = {}) {
  return {
    ...item,
    student_name: fields.clientName || item.student_name,
    student_id: fields.studentId || item.student_id || null,
    dob: fields.dob || item.dob || null,
    course_name: fields.course || item.course_name,
    provider_name: fields.provider || item.provider_name,
    start_date: fields.start || item.start_date || null,
    end_date: fields.end || item.end_date || null,
    tuition_fee:
      fields.tuitionFee != null && fields.tuitionFee !== ""
        ? Number(fields.tuitionFee)
        : item.tuition_fee,
    application_course_id:
      fields.applicationCourseId || item.application_course_id || null,
  };
}

export function isBatchItemFieldMissing(key, fields = {}) {
  if (key === "studentId") {
    return !String(fields.studentId || "").trim();
  }
  if (key === "dob") {
    return !toInquiryInputDate(fields.dob);
  }
  if (key === "clientName") return !String(fields.clientName || "").trim();
  if (key === "course") return !String(fields.course || "").trim();
  if (key === "provider") return !String(fields.provider || "").trim();
  if (key === "intake") return !String(fields.intake || "").trim();
  if (key === "start") return !toInquiryInputDate(fields.start);
  if (key === "end") return !toInquiryInputDate(fields.end);
  return false;
}

export function isInquiryComplete(fields = {}) {
  return getMissingInquiryFields(fields).length === 0;
}

export function buildBatchItemUpdatePayload(values = {}) {
  const payload = {
    student_id: String(values.studentId || "").trim(),
    dob: toInquiryInputDate(values.dob) || null,
    course_name: String(values.course || "").trim(),
    intake: String(values.intake || "").trim(),
    start_date: toInquiryInputDate(values.start) || String(values.start || ""),
    end_date: toInquiryInputDate(values.end) || String(values.end || ""),
  };

  if (values.courseId) {
    payload.course_id = Number(values.courseId);
  }

  return payload;
}

export function buildInquiryUpdatePayload(application, values, overrides = {}) {
  const base = {
    ...getInquiryFieldsFromApplication(application),
    ...overrides,
  };

  const toIdString = (value) => {
    if (value == null || value === "") return "";
    return String(value);
  };

  const applicationCourseId =
    base.applicationCourseId != null && base.applicationCourseId !== ""
      ? Number(base.applicationCourseId)
      : null;

  return {
    application_course_id: Number.isFinite(applicationCourseId)
      ? applicationCourseId
      : null,
    application_id: toIdString(base.applicationId),
    client_id: toIdString(base.clientId),
    course_id: toIdString(values.courseId ?? base.courseId),
    course_name: String(values.course || base.course || "").trim(),
    intake: String(values.intake || base.intake || "").trim(),
    intake_id: toIdString(overrides.intakeId ?? values.intakeId) || undefined,
    dob: toInquiryInputDate(values.dob) || String(values.dob || ""),
    end_date: toInquiryInputDate(values.end) || String(values.end || ""),
    provider_id: toIdString(base.providerId),
    start_date: toInquiryInputDate(values.start) || String(values.start || ""),
    student_id: String(values.studentId || "").trim(),
    tuition_fee: String(values.tuitionFee ?? "").trim(),
    commissionable_tuition_fee: String(
      values.commissionableTuitionFee ?? values.commissionable_tuition_fee ?? "",
    ).trim(),
    type: base.type || "education",
  };
}
